// Floating team-chat widget — a Messenger-style bubble pinned to the
// bottom-right corner that follows the user across every module. Click it
// to pop open a compact chat panel (the full TeamChatView, embedded) right
// where you are, without leaving the current screen. New incoming messages
// surface as a corner toast (handled at the App level) and bump the bubble's
// unread badge.
//
// The popped-open panel is DRAGGABLE by its header (same UX as the floating
// Calculator) so it can be moved out of the way — Lissette 2026-06-23. The
// bubble itself stays pinned bottom-right.
//
// Hidden when the user is already on the full "Team Chat" view (to avoid two
// live chat instances) and when chat isn't available for the account.

import { useState, useEffect, useRef } from 'react';
import { MessageCircle, X, Maximize2 } from 'lucide-react';
import TeamChatView from './TeamChatView.jsx';

const PANEL_W = 700, PANEL_H = 540;

export default function ChatWidget({ currentUser, lang = 'en', unread = 0, hidden = false, onExpand, onNavigate }) {
  const es = lang === 'en' ? false : true;
  const [open, setOpen] = useState(false);

  // Free-drag position. `null` until first open → falls back to the default
  // bottom-right anchor; once moved it sticks where the user dropped it.
  const [pos, setPos] = useState(null);
  const drag = useRef(null);

  useEffect(() => {
    if (open && pos == null && typeof window !== 'undefined') {
      const w = window.innerWidth, h = window.innerHeight;
      const pw = Math.min(PANEL_W, w - 32), ph = Math.min(PANEL_H, h - 120);
      setPos({ x: Math.max(12, w - pw - 20), y: Math.max(12, h - ph - 84) });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const onHeaderDown = (e) => {
    if (e.target.closest('[data-no-drag]')) return;
    const base = pos || { x: 0, y: 0 };
    drag.current = { dx: e.clientX - base.x, dy: e.clientY - base.y };
    e.preventDefault();
  };
  useEffect(() => {
    const move = (e) => {
      if (!drag.current) return;
      const w = window.innerWidth, h = window.innerHeight;
      setPos({
        x: Math.min(Math.max(0, e.clientX - drag.current.dx), w - 80),
        y: Math.min(Math.max(0, e.clientY - drag.current.dy), h - 40),
      });
    };
    const up = () => { drag.current = null; };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
    return () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
  }, []);

  if (hidden) return null;

  return (
    <>
      {/* Floating panel */}
      {open && (
        <div
          style={{
            position: 'fixed',
            ...(pos ? { left: pos.x, top: pos.y } : { right: 20, bottom: 84 }),
            zIndex: 45,
            width: 'min(700px, calc(100vw - 32px))',
            height: 'min(540px, calc(100vh - 120px))',
            background: 'var(--c-bg2,#0b1622)', border: '1px solid var(--c-border2,#1f3f60)',
            borderRadius: 16, boxShadow: '0 24px 80px rgba(0,0,0,.55)', display: 'flex', flexDirection: 'column', overflow: 'hidden',
          }}
        >
          <div onMouseDown={onHeaderDown} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '.5rem .75rem', borderBottom: '1px solid var(--c-border,#1a3550)', background: 'var(--c-surface,#0c1c2e)', cursor: 'move', userSelect: 'none' }}>
            <span style={{ fontWeight: 700, fontSize: '.85rem', color: 'var(--c-white,#eef6ff)' }}>{es ? 'Chat de Equipo' : 'Team Chat'}</span>
            <div style={{ display: 'flex', gap: 4 }}>
              {onExpand && (
                <button data-no-drag title={es ? 'Abrir en pantalla completa' : 'Open full screen'} onClick={() => { setOpen(false); onExpand(); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', padding: 4, display: 'flex' }}><Maximize2 size={15} /></button>
              )}
              <button data-no-drag title={es ? 'Cerrar' : 'Close'} onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-text3,#45688a)', padding: 4, display: 'flex' }}><X size={16} /></button>
            </div>
          </div>
          <div style={{ flex: 1, minHeight: 0, padding: 10 }}>
            <TeamChatView currentUser={currentUser} lang={lang} embedded
              onNavigate={onNavigate ? (link) => { setOpen(false); onNavigate(link); } : undefined} />
          </div>
        </div>
      )}

      {/* Bubble */}
      <button
        onClick={() => setOpen((o) => !o)}
        title={es ? 'Chat de Equipo' : 'Team Chat'}
        style={{
          position: 'fixed', right: 20, bottom: 20, zIndex: 46,
          width: 52, height: 52, borderRadius: '50%', border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg,#00b4d8,#0077b6)', color: '#fff',
          boxShadow: '0 8px 24px rgba(0,153,204,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        {open ? <X size={22} /> : <MessageCircle size={24} />}
        {!open && unread > 0 && (
          <span style={{ position: 'absolute', top: -2, right: -2, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 999, background: '#ff3d6b', color: '#fff', fontSize: '.62rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid var(--c-bg,#070d14)' }}>
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>
    </>
  );
}
